import Vue from 'vue'
import Vuex from 'vuex'
import { Command } from '@/Command'
import User from '@/User'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat' // import plugin
import 'dayjs/locale/zh-cn' // import locale
import * as _ from 'lodash'
import CloudGpxInfo from './CloudGpxInfo'
import SubscriptionValidResult from './SubscriptionValidResult'
import StorageHelper from './StorageHelper'

dayjs.extend(localizedFormat) // use plugin

Vue.use(Vuex)

const claimsToBeSubscribed = function(state: any) {
    return state.user !== null && state.user.isSubscribed === true
}
const isSubscribed = function(state: any) {
    const res = claimsToBeSubscribed(state) && 
        !(state.subscriptionValidResult !== null && state.subscriptionValidResult.isValid === false);
    return res;
}

const getCloudGpxs = function(state: any): CloudGpxInfo[] {
    if(!state.user) {
        return []
    }
    if(Array.isArray(state.user.cloudGpxs)) {
        return state.user.cloudGpxs
    }
    const privateGpxs = Array.isArray(state.user.privateGpxs) ? state.user.privateGpxs : []
    const publicGpxs = Array.isArray(state.user.publicGpxs) ? state.user.publicGpxs : []
    return [...privateGpxs, ...publicGpxs]
}

const removeCloudGpx = function(state: any, gpx: CloudGpxInfo) {
    if(state.user?.cloudGpxs) {
        const index = state.user.cloudGpxs.findIndex((m: CloudGpxInfo) => m.url === gpx.url && m.public === gpx.public)
        if(index >= 0) {
            state.user.cloudGpxs.splice(index, 1)
        }
    }
}

const saveCloudGpx = function(state: any, newGpx: CloudGpxInfo) {
    if(state.user) {
        if(!state.user.cloudGpxs) {
            state.user.cloudGpxs = []
        }
        const existing = _.find(state.user.cloudGpxs, s => s.url === newGpx.url && s.public === newGpx.public)
        if(existing) {
            existing.modified = newGpx.modified
        } else {
            state.user.cloudGpxs.push(newGpx)
            state.user.cloudGpxs = _.orderBy(state.user.cloudGpxs, s => s.name)
        }
    }
}

const store = new Vuex.Store({
    state: {
        loggedInUser: null,
        userCommands: [] as Command[],
        user: null as User | null,
        subscriptionExpiry:  null as Date|null,
        loginAttempted: false,
        currentTrackValue: '',
        showSubscriptionInvalid: false,
        subscriptionValidResult: null as SubscriptionValidResult|null,
        showingPricingSidebar: false
    },
    getters: {
        showingPricingSidebar: state => {
            return state.showingPricingSidebar;
        },
        userCommands: state => {
            return state.userCommands
        },
        claimsToBeSubscribed: state => {
            return claimsToBeSubscribed(state);
        },
        isSubscribed: state => {
            return isSubscribed(state)
        },
        isOrMightBeSubscribed: state => {
            return (!state.loginAttempted) || isSubscribed(state) // if they're currently logging in (loginAttempted = false), then they might still be a subscriber
        },
        isCustomer: state => {
            return state.user !== null && state.user.isCustomer
        },
        isSubscriber: state => {
            return state.user !== null && state.user.isSubscriber;
        },  
        isExpired: state => {
            return state.subscriptionExpiry !== null && state.subscriptionExpiry < new Date()
        },
        expiryDateString: state => {
            return state.subscriptionExpiry === null ? "" : dayjs(state.subscriptionExpiry).format('ddd, ll')
        },
        expiryDateStringWithTime: state => {
            return state.subscriptionExpiry === null ? "" : dayjs(state.subscriptionExpiry).format('ddd, ll,  h:mm a')
        },
        hasUserAlreadyUsedUpFreeTrial: state => {
            return state.user === null ? false : state.user.hasUserAlreadyUsedUpFreeTrial
        },
        loggedInUser: state => { console.log('state.user: ', state.user); return state.user },
        isUserLoggedIn: state => { return state.user !== null },
        loggedInUserEmail: state => { return state.user?.email },
        cloudGpxs: state => { return getCloudGpxs(state) },
        hasCloudGpxs: state => { return getCloudGpxs(state).length > 0 },
        hasPrivateGpxs: state => { return state.user && state.user.privateGpxs && state.user.privateGpxs.length > 0},
        hasPublicGpxs: state => { return state.user && state.user.publicGpxs && state.user.publicGpxs.length > 0},
        loginAttempted: state => { return state.loginAttempted },
        currentTrackValue: state => { return state.currentTrackValue },
        showSubscriptionInvalid: state => { return state.showSubscriptionInvalid },
        subscriptionValidManageSubscriptionUrl: state => { return state.subscriptionValidResult !== null ? state.subscriptionValidResult.manageSubscriptionUrl : null; },
        routerUrl: state => {
            if (state.user && state.user.hasRouterUrlOverride && state.user.routerUrlOverride) {
                return state.user.routerUrlOverride;
            } else {
                return "https://router3.gpxplanner.app";
            }
        },
        elevationUrl: state => {
            if (state.user && state.user.hasHeightServerUrlOverride && state.user.heightServerUrlOverride) {
                return state.user.heightServerUrlOverride;
            } else {
                return "https://raster-height-343826512547.us-central1.run.app";
            }
        }
    },
    mutations: {
        showingPricingSidebar(state, showing) {
            state.showingPricingSidebar = showing;
            StorageHelper.setItem('showingPricingSidebar', `${showing}`);
        },
        pushCommand(state, msg: Command) {
            state.userCommands.push(msg)
        },
        handledCommand(state, id: string) {
            const index = state.userCommands.findIndex((m) => m.id === id)
            if (index >= 0) {
                state.userCommands.splice(index, 1)
            }
        },
        login(state, user: User) {
            if(!(user as any).cloudGpxs) {
                const privateGpxs = Array.isArray(user.privateGpxs) ? user.privateGpxs : []
                const publicGpxs = Array.isArray(user.publicGpxs) ? user.publicGpxs : []
                ;(user as any).cloudGpxs = [...privateGpxs, ...publicGpxs]
            }
            state.user = user
            if(user.subscriptionExpiry) {
                state.subscriptionExpiry = new Date(user.subscriptionExpiry)
            } else {
                state.subscriptionExpiry = null;
            }
        },
        loginAttempted(state) {
            state.loginAttempted = true
        },
        logout(state) {
            state.user = null
            state.subscriptionExpiry = null
        },
        setSubscriptionExpiry(state, date: Date) {
            state.subscriptionExpiry = date
        },
        setSubscriptionInvalid(state, result: SubscriptionValidResult) {
            state.subscriptionValidResult = result;
            state.showSubscriptionInvalid = true
        },
        dismissSubscriptionInvalid(state) {
            state.showSubscriptionInvalid = false; //just dismisses the dialog
            // don't set subscriptionValidResult to null, otherwise they will gain access...
        },
        deletePublicGpx(state, gpxUrl: string) {
            const toDelete = getCloudGpxs(state).find(m => m.url === gpxUrl && m.public)
            if(toDelete) {
                removeCloudGpx(state, toDelete)
            }
        },
        deletePrivateGpx(state, gpxUrl: string) {
            const toDelete = getCloudGpxs(state).find(m => m.url === gpxUrl && !m.public)
            if(toDelete) {
                removeCloudGpx(state, toDelete)
            }
        },
        deleteCloudGpx(state, gpx: CloudGpxInfo) {
            removeCloudGpx(state, gpx)
        },
        savePublicGpx(state, newGpx: CloudGpxInfo) {
            saveCloudGpx(state, newGpx)
        },
        savePrivateGpx(state, newGpx: CloudGpxInfo) {
            saveCloudGpx(state, newGpx)
        },
        saveCloudGpx(state, newGpx: CloudGpxInfo) {
            saveCloudGpx(state, newGpx)
        },
        setCurrentTrackValue(state, id: string) {
            state.currentTrackValue = id
        },
        setIsLinkedToRideWithGps(state, isLinked: boolean) {
            if(state.user) {
                state.user.isLinkedToRideWithGps = isLinked
            }
        }
    }
})

export default store
